import React, { useContext } from "react";
import { FaCheckCircle, FaLightbulb, FaHandsHelping, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import "../TrainingGlobal.css";
import BannerImage from "./iso.jpg";

const ISO45001: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const handleBooking = () => {
    if (isAuthenticated) {
      navigate('/booking/iso-45001');
    } else {
      navigate('/login', { state: { from: '/booking/iso-45001' } });
    }
  };

  return (
    <div className="training-page">
      <div className="training-max-width">

        {/* --- HERO --- */}
        <div className="training-hero" style={{ backgroundImage: `url(${BannerImage})` }}>
          <span className="hero-tag">ISO Training</span>
          <h1 className="hero-title">ISO 45001 Auditor</h1>
          <p className="hero-subtitle">
            Ensuring occupational health and safety compliance
          </p>
          <button onClick={handleBooking} className="btn-hero">
            Book Now <span style={{ fontSize: '1.2em' }}>→</span>
          </button>
        </div>

        {/* --- CONTENT --- */}
        <div className="detail-content">

          <div className="content-article">
            <div className="article-section">
              <h2 className="article-heading">Introduction to ISO 45001:2018</h2>
              <p className="article-text">
                ISO 45001:2018 is an international standard for occupational health and safety management systems. It provides a framework for improving workplace safety, reducing risks, and enhancing employee well-being. The standard is applicable to organizations of all sizes and industries, helping them create safer work environments. By implementing ISO 45001, organizations demonstrate their commitment to protecting employees and complying with legal requirements.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Key Principles of ISO 45001:2018</h2>
              <p className="article-text">
                The key principles include leadership commitment, worker participation, risk-based thinking, and continuous improvement. Leadership commitment ensures that safety is prioritized at all organizational levels. Worker participation fosters collaboration and empowers employees to contribute to safety initiatives. Risk-based thinking focuses on identifying and mitigating risks proactively. Continuous improvement ensures that safety measures evolve to address emerging challenges and opportunities.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Occupational Health and Safety Management</h2>
              <p className="article-text">
                Occupational health and safety management involves identifying hazards, assessing risks, and implementing controls to create a safe and healthy work environment. This includes providing training, conducting regular inspections, and maintaining safety equipment. Effective management reduces workplace accidents, enhances employee morale, and improves productivity. Organizations that prioritize health and safety build trust with employees and stakeholders.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Risk Assessment and Hazard Identification</h2>
              <p className="article-text">
                Risk assessment and hazard identification are critical for preventing workplace accidents. These processes involve evaluating potential risks and implementing measures to mitigate them. For example, identifying electrical hazards and implementing lockout/tagout procedures reduces the risk of shocks. Regularly updating risk assessments ensures that new hazards are addressed promptly, maintaining a safe work environment.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Structure of ISO 45001:2018</h2>
              <p className="article-text">
                The structure of ISO 45001:2018 follows the High-Level Structure (HLS), making it compatible with other ISO standards. It includes clauses such as context of the organization, leadership, planning, support, operation, performance evaluation, and improvement. This structure ensures a systematic approach to managing occupational health and safety. Organizations can integrate ISO 45001 with other management systems, such as ISO 9001 and ISO 14001, for a unified approach to compliance and performance.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Emergency Preparedness and Response</h2>
              <p className="article-text">
                Emergency preparedness and response involve planning for potential emergencies, conducting drills, and ensuring resources are available to handle incidents effectively. For example, fire drills and evacuation plans prepare employees to respond quickly during emergencies. Regularly reviewing and updating emergency plans ensures that organizations are ready to address evolving risks. Effective preparedness minimizes the impact of emergencies on employees and operations.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Incident Investigation and Reporting</h2>
              <p className="article-text">
                Incident investigation and reporting help identify root causes of accidents and implement corrective actions. This process ensures continuous improvement in workplace safety. For example, investigating a slip-and-fall incident may reveal the need for better floor maintenance or signage. Transparent reporting fosters a culture of accountability and learning, reducing the likelihood of recurring incidents.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Worker Participation and Consultation</h2>
              <p className="article-text">
                Worker participation and consultation are essential for identifying risks and implementing effective safety measures. Engaging employees fosters a culture of safety and collaboration. For example, involving workers in safety committees and decision-making processes ensures that their insights and concerns are addressed. Active participation enhances compliance and builds trust between employees and management.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Continuous Improvement</h2>
              <p className="article-text">
                Continuous improvement involves regularly reviewing and updating safety measures to address emerging risks and enhance the effectiveness of the occupational health and safety management system. This includes analyzing performance data, conducting audits, and implementing corrective actions. A commitment to continuous improvement ensures that organizations stay ahead of challenges and maintain a safe work environment.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Certification Process</h2>
              <p className="article-text">
                The certification process involves an external audit to verify compliance with ISO 45001:2018 requirements. Achieving certification demonstrates an organization's commitment to workplace safety. The process includes a gap analysis, implementation of corrective actions, and a final audit by a certification body. Certification enhances credibility, improves stakeholder confidence, and provides a competitive advantage in the market.
              </p>
            </div>
          </div>

          {/* --- SIDEBAR --- */}
          <div className="content-sidebar">
            <div className="sidebar-widget">
              <h4 className="widget-title">Key Elements</h4>
              <ul className="key-list">
                <li><FaCheckCircle className="check-icon" /> Hazard Identification</li>
                <li><FaLightbulb className="check-icon" /> Risk Assessment</li>
                <li><FaHandsHelping className="check-icon" /> Emergency Readiness</li>
                <li><FaBullhorn className="check-icon" /> Worker Participation</li>
              </ul>
            </div>

            <div className="sidebar-widget">
              <h4 className="widget-title">Why ISO 45001?</h4>
              <p style={{ color: '#aaa', lineHeight: '1.6', fontSize: '0.9rem' }}>
                Ensure occupational health and safety to protect your workforce.
              </p>
            </div>

            <div className="sidebar-widget" style={{ background: 'linear-gradient(135deg, #111 0%, #222 100%)', border: '1px solid #333' }}>
              <h4 className="widget-title">Get Certified</h4>
              <p style={{ color: '#ccc', marginBottom: '20px', fontSize: '0.9rem' }}>
                Start your journey towards standard compliance.
              </p>

              <button
                onClick={handleBooking}
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Book Now
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ISO45001;

